import { useCallback, useEffect, useRef, useState, type ImgHTMLAttributes } from "react";
import { focusFit, type FocusBox } from "@/lib/image-focus";

/**
 * Bild som visas inzoomad på själva prylen när AI:n har markerat var den finns.
 * Ryms inte prylen i rutan visas hela bilden i stället – inget kapas bort.
 */
export function FocusedImage({
  focus,
  fallbackFit = "contain",
  className,
  style,
  ...rest
}: {
  focus?: FocusBox | null;
  /** Hur bilden visas utan fokusruta – kort vill fylla rutan, gallerier inte. */
  fallbackFit?: "cover" | "contain";
} & ImgHTMLAttributes<HTMLImageElement>) {
  const ref = useRef<HTMLImageElement | null>(null);
  const [imageAspect, setImageAspect] = useState(0);
  const [frameAspect, setFrameAspect] = useState(0);

  const measure = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    if (rect.width && rect.height) setFrameAspect(rect.width / rect.height);
    if (el.naturalWidth && el.naturalHeight) setImageAspect(el.naturalWidth / el.naturalHeight);
  }, []);

  useEffect(() => {
    measure();
    const el = ref.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [measure]);

  const fit = focusFit(focus, frameAspect, imageAspect, fallbackFit);

  return (
    <img
      {...rest}
      ref={ref}
      onLoad={(e) => {
        measure();
        rest.onLoad?.(e);
      }}
      style={{
        ...style,
        objectFit: fit.objectFit,
        objectPosition: fit.objectPosition,
      }}
      className={className}
    />
  );
}
